import axios from 'axios';

// REACT_APP_API_URL se lee en build-time (convención de Create React App).
// En producción la API vive bajo el mismo dominio para evitar contenido
// mixto; en desarrollo se conserva el backend local en el puerto 5000.
const API_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');
export const UPLOADS_BASE_URL = API_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

// Las siguientes funciones se mantienen como no-op por compatibilidad
// La autenticación ahora se maneja vía httpOnly cookie
export const setAuthToken = () => {};
export const getAuthToken = () => null;
export const clearAuthToken = () => {};

export const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al conectar con el servidor' };
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al conectar con el servidor' };
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al conectar con el servidor' };
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignorar error de logout
    }
    clearAuthToken();
  },

  verifySession: async () => {
    try {
      const response = await api.get('/auth/verify-session');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Sesión no válida' };
    }
  },
};

export const clientesService = {
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`/clientes?${params}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener clientes' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/clientes/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener cliente' };
    }
  },

  create: async (clienteData) => {
    try {
      const response = await api.post('/clientes', clienteData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear cliente' };
    }
  },

  update: async (id, clienteData) => {
    try {
      const response = await api.put(`/clientes/${id}`, clienteData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar cliente' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/clientes/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar cliente' };
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/clientes/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estadísticas' };
    }
  }
};

export const proveedoresService = {
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`/proveedores?${params}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener proveedores' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/proveedores/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener proveedor' };
    }
  },

  create: async (proveedorData) => {
    try {
      const response = await api.post('/proveedores', proveedorData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear proveedor' };
    }
  },

  update: async (id, proveedorData) => {
    try {
      const response = await api.put(`/proveedores/${id}`, proveedorData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar proveedor' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/proveedores/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar proveedor' };
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/proveedores/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estadísticas' };
    }
  },

  getBancos: async () => {
    try {
      const response = await api.get('/proveedores/bancos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener bancos' };
    }
  },

  getActivos: async () => {
    try {
      const response = await api.get('/proveedores/activos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener proveedores activos' };
    }
  },

  addCuentaBancaria: async (proveedorId, cuentaData) => {
    try {
      const response = await api.post(`/proveedores/${proveedorId}/cuentas`, cuentaData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al agregar cuenta bancaria' };
    }
  },

  updateCuentaBancaria: async (proveedorId, cuentaId, cuentaData) => {
    try {
      const response = await api.put(`/proveedores/${proveedorId}/cuentas/${cuentaId}`, cuentaData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar cuenta bancaria' };
    }
  },

  deleteCuentaBancaria: async (proveedorId, cuentaId) => {
    try {
      const response = await api.delete(`/proveedores/${proveedorId}/cuentas/${cuentaId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar cuenta bancaria' };
    }
  },

  addPagoMovil: async (proveedorId, pagoData) => {
    try {
      const response = await api.post(`/proveedores/${proveedorId}/pagos-movil`, pagoData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al agregar pago móvil' };
    }
  },

  updatePagoMovil: async (proveedorId, pagoId, pagoData) => {
    try {
      const response = await api.put(`/proveedores/${proveedorId}/pagos-movil/${pagoId}`, pagoData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar pago móvil' };
    }
  },

  deletePagoMovil: async (proveedorId, pagoId) => {
    try {
      const response = await api.delete(`/proveedores/${proveedorId}/pagos-movil/${pagoId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar pago móvil' };
    }
  }
};

export const categoriasService = {
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`/categorias?${params}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener categorías' };
    }
  },

  getActivas: async () => {
    try {
      const response = await api.get('/categorias/activas');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener categorías activas' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/categorias/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener categoría' };
    }
  },

  create: async (categoriaData) => {
    try {
      const response = await api.post('/categorias', categoriaData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear categoría' };
    }
  },

  update: async (id, categoriaData) => {
    try {
      const response = await api.put(`/categorias/${id}`, categoriaData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar categoría' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/categorias/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar categoría' };
    }
  }
};

export const articulosService = {
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`/articulos?${params}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener artículos' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/articulos/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener artículo' };
    }
  },

  getStock: async (id) => {
    try {
      const response = await api.get(`/articulos/${id}/stock`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener stock' };
    }
  },

  create: async (articuloData) => {
    try {
      const response = await api.post('/articulos', articuloData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear artículo' };
    }
  },

  update: async (id, articuloData) => {
    try {
      const response = await api.put(`/articulos/${id}`, articuloData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar artículo' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/articulos/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar artículo' };
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/articulos/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estadísticas' };
    }
  },

  getActivos: async () => {
    try {
      const response = await api.get('/articulos/activos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener artículos activos' };
    }
  },

  updateStock: async (id, cantidad, operacion) => {
    try {
      const response = await api.put(`/articulos/${id}/stock`, { cantidad, operacion });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar stock' };
    }
  },

  uploadImagen: async (file) => {
    try {
      const formData = new FormData();
      formData.append('imagen', file);
      const response = await api.post('/articulos/upload-imagen', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al subir imagen' };
    }
  },

  getImagenes: async (articuloId) => {
    try {
      const response = await api.get(`/articulos/${articuloId}/imagenes`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener imágenes' };
    }
  },

  addImagen: async (articuloId, file, esPrincipal = false) => {
    try {
      const formData = new FormData();
      formData.append('imagen', file);
      formData.append('es_principal', esPrincipal);
      const response = await api.post(`/articulos/${articuloId}/imagenes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al agregar imagen' };
    }
  },

  deleteImagen: async (articuloId, imagenId) => {
    try {
      const response = await api.delete(`/articulos/${articuloId}/imagenes/${imagenId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar imagen' };
    }
  },

  setPrincipalImagen: async (articuloId, imagenId) => {
    try {
      const response = await api.put(`/articulos/${articuloId}/imagenes/${imagenId}/principal`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al establecer imagen principal' };
    }
  }
};

// ===== UBICACIÓN FÍSICA DE ARTÍCULOS (ALMACÉN / ZONA / ÁREA / RACK) =====
export const ubicacionesAlmacenService = {
  getArbol: async () => {
    try {
      const response = await api.get('/ubicaciones-almacen');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener ubicaciones de almacén' };
    }
  },

  create: async ({ nivel, nombre, padre_id }) => {
    try {
      const response = await api.post('/ubicaciones-almacen', { nivel, nombre, padre_id });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear ubicación' };
    }
  },

  update: async (id, { nombre, activo }) => {
    try {
      const response = await api.put(`/ubicaciones-almacen/${id}`, { nombre, activo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar ubicación' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/ubicaciones-almacen/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al desactivar ubicación' };
    }
  }
};

// ===== PRECIOS DE ARTÍCULO POR PROVEEDOR =====
export const articulosPreciosProveedorService = {
  getAll: async (articuloId) => {
    try {
      const response = await api.get(`/articulos-precios-proveedor/${articuloId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener precios por proveedor' };
    }
  },

  guardar: async (articuloId, datos) => {
    try {
      const response = await api.post(`/articulos-precios-proveedor/${articuloId}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar el precio' };
    }
  },

  eliminar: async (id) => {
    try {
      const response = await api.delete(`/articulos-precios-proveedor/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar el precio' };
    }
  }
};

export const serviciosService = {
  getAll: async (params = {}) => {
    const response = await api.get('/servicios', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/servicios/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/servicios', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/servicios/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/servicios/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/servicios/stats/general');
    return response.data;
  }
};

export const subserviciosService = {
  getAll: async (params = {}) => {
    const response = await api.get('/subservicios', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/subservicios/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/subservicios', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/subservicios/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/subservicios/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/subservicios/stats/general');
    return response.data;
  }
};

export const categoriasSolicitudService = {
  getAll: async (params = {}) => {
    const response = await api.get('/categorias-solicitud', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/categorias-solicitud/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/categorias-solicitud', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/categorias-solicitud/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/categorias-solicitud/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/categorias-solicitud/stats/general');
    return response.data;
  }
};

export const empresasService = {
  getAll: async (params = {}) => {
    const response = await api.get('/empresas', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/empresas/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/empresas', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/empresas/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/empresas/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/empresas/stats/general');
    return response.data;
  }
};

export const solicitudesService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/solicitudes', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener solicitudes' };
    }
  },

  getById: async (id) => {
    const response = await api.get(`/solicitudes/${id}`);
    return response.data;
  },

  getArticulos: async (solicitudId) => {
    try {
      const response = await api.get(`/solicitudes/${solicitudId}/articulos`);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener artículos de solicitud ${solicitudId}:`, error);
      throw error.response?.data || { message: 'Error al obtener artículos de la solicitud' };
    }
  },

  getConfig: async () => {
    try {
      const response = await api.get('/solicitudes/config');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener configuración' };
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/solicitudes/stats/general');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estadísticas' };
    }
  },

  create: async (data) => {
    try {
      const solicitudData = {
        ...data,
        trabajos: data.trabajos?.map(t => ({
          nombre_trabajo: t.nombre_trabajo,
          descripcion_trabajo: t.descripcion_trabajo || '',
          precio_mano_obra: t.precio_mano_obra || 0,
          articulos: t.articulos?.map(art => ({
            id_articulo: art.id_articulo || null,
            cantidad: art.cantidad || 1,
            status: art.status || 'solicitud_nueva',
            via: art.via || null,
            tiempo_entrega: art.tiempo_entrega || null,
            observacion: art.observacion || null,
            costo: art.costo || 0,
            precio_venta: art.precio_venta || 0
          })) || []
        })) || [],
        articulos: data.articulos?.map(art => ({
          id_articulo: art.id_articulo || null,
          cantidad: art.cantidad || 1,
          status: art.status || 'solicitud_nueva',
          via: art.via || null,
          tiempo_entrega: art.tiempo_entrega || null,
          observacion: art.observacion || null,
          costo: art.costo || 0,
          precio_venta: art.precio_venta || 0
        })) || []
      };
      const response = await api.post('/solicitudes', solicitudData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear solicitud' };
    }
  },

  update: async (id, data) => {
    try {
      const solicitudData = { ...data };
      if (data.trabajos !== undefined) {
        solicitudData.trabajos = data.trabajos.map(t => ({
          nombre_trabajo: t.nombre_trabajo,
          descripcion_trabajo: t.descripcion_trabajo || '',
          precio_mano_obra: t.precio_mano_obra || 0,
          articulos: t.articulos?.map(art => ({
            id_articulo: art.id_articulo || null,
            cantidad: art.cantidad || 1,
            status: art.status || 'solicitud_nueva',
            via: art.via || null,
            tiempo_entrega: art.tiempo_entrega || null,
            observacion: art.observacion || null,
            costo: art.costo || 0,
            precio_venta: art.precio_venta || 0
          })) || []
        }));
      }
      if (data.articulos !== undefined) {
        solicitudData.articulos = data.articulos.map(art => ({
          id_articulo: art.id_articulo || null,
          cantidad: art.cantidad || 1,
          status: art.status || 'solicitud_nueva',
          via: art.via || null,
          tiempo_entrega: art.tiempo_entrega || null,
          observacion: art.observacion || null,
          costo: art.costo || 0,
          precio_venta: art.precio_venta || 0
        }));
      }
      const response = await api.put(`/solicitudes/${id}`, solicitudData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar solicitud' };
    }
  },

  updateArticulo: async (solicitudId, articuloId, data) => {
    try {
      const response = await api.put(`/solicitudes/${solicitudId}/articulos/${articuloId}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar artículo' };
    }
  },

  delete: async (id) => {
    const response = await api.delete(`/solicitudes/${id}`);
    return response.data;
  },

  getEstadosReales: async () => {
    try {
      const response = await api.get('/solicitudes/estados-reales');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estados' };
    }
  },

  getFacturacion: async (id) => {
    try {
      const response = await api.get(`/solicitudes/${id}/facturacion`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener trazabilidad de cobro' };
    }
  }
};

export const solicitudesSeguimientoService = {
  getBySolicitud: async (solicitudId) => {
    try {
      const response = await api.get(`/solicitudes-seguimiento/${solicitudId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener el seguimiento' };
    }
  },

  create: async (solicitudId, { tipo, descripcion, fotos = [] }) => {
    try {
      const formData = new FormData();
      formData.append('tipo', tipo);
      formData.append('descripcion', descripcion);
      fotos.forEach(f => formData.append('fotos', f));
      const response = await api.post(`/solicitudes-seguimiento/${solicitudId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al registrar el seguimiento' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/solicitudes-seguimiento/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar el registro' };
    }
  }
};

export const usuariosService = {
  getAll: async () => {
    try {
      const response = await api.get('/usuarios');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener usuarios' };
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/usuarios', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear usuario' };
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/usuarios/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar usuario' };
    }
  },

  setGrupos: async (id, grupoIds) => {
    try {
      const response = await api.put(`/usuarios/${id}/grupos`, { grupo_ids: grupoIds });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al asignar grupos' };
    }
  },

  getPermisos: async (id) => {
    try {
      const response = await api.get(`/usuarios/${id}/permisos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener permisos' };
    }
  },

  setPermisos: async (id, permisos) => {
    try {
      const response = await api.put(`/usuarios/${id}/permisos`, { permisos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar permisos' };
    }
  },

  getPermisosEfectivos: async (id) => {
    try {
      const response = await api.get(`/usuarios/${id}/permisos-efectivos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener permisos efectivos' };
    }
  }
};

export const rolesService = {
  getAll: async () => {
    try {
      const response = await api.get('/roles');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener roles' };
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/roles', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear rol' };
    }
  },
  update: async (codigo, data) => {
    try {
      const response = await api.put(`/roles/${codigo}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar rol' };
    }
  },
  getPermisos: async (codigo) => {
    try {
      const response = await api.get(`/roles/${codigo}/permisos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener permisos' };
    }
  },
  setPermisos: async (codigo, permisos) => {
    try {
      const response = await api.put(`/roles/${codigo}/permisos`, { permisos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar permisos' };
    }
  }
};

export const departamentosService = {
  getAll: async () => {
    try {
      const response = await api.get('/departamentos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener departamentos' };
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/departamentos', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear departamento' };
    }
  },
  update: async (codigo, data) => {
    try {
      const response = await api.put(`/departamentos/${codigo}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar departamento' };
    }
  },
  getPermisos: async (codigo) => {
    try {
      const response = await api.get(`/departamentos/${codigo}/permisos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener permisos' };
    }
  },
  setPermisos: async (codigo, permisos) => {
    try {
      const response = await api.put(`/departamentos/${codigo}/permisos`, { permisos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar permisos' };
    }
  }
};

export const gruposService = {
  getAll: async () => {
    try {
      const response = await api.get('/grupos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener grupos' };
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/grupos', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear grupo' };
    }
  },
  update: async (id, data) => {
    try {
      const response = await api.put(`/grupos/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar grupo' };
    }
  },
  getMiembros: async (id) => {
    try {
      const response = await api.get(`/grupos/${id}/usuarios`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener miembros' };
    }
  },
  agregarMiembro: async (id, usuarioId) => {
    try {
      const response = await api.post(`/grupos/${id}/usuarios/${usuarioId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al agregar miembro' };
    }
  },
  quitarMiembro: async (id, usuarioId) => {
    try {
      const response = await api.delete(`/grupos/${id}/usuarios/${usuarioId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al quitar miembro' };
    }
  },
  getPermisos: async (id) => {
    try {
      const response = await api.get(`/grupos/${id}/permisos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener permisos' };
    }
  },
  setPermisos: async (id, permisos) => {
    try {
      const response = await api.put(`/grupos/${id}/permisos`, { permisos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar permisos' };
    }
  }
};

export const modulosService = {
  getAll: async () => {
    try {
      const response = await api.get('/modulos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener módulos' };
    }
  }
};

export const solicitudesArchivosService = {
  getBySolicitudId: async (solicitudId) => {
    const response = await api.get(`/solicitudes-archivos/${solicitudId}`);
    return response.data;
  },

  upload: async (solicitudId, file) => {
    const formData = new FormData();
    formData.append('archivo', file);
    const response = await api.post(`/solicitudes-archivos/${solicitudId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  delete: async (archivoId) => {
    const response = await api.delete(`/solicitudes-archivos/${archivoId}`);
    return response.data;
  }
};

export const solicitudesArticulosService = {
  getArticulos: async (solicitudId) => {
    try {
      const response = await api.get(`/solicitudes/${solicitudId}/articulos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener artículos' };
    }
  },

  update: async (solicitudId, articuloId, data) => {
    try {
      const response = await api.put(`/solicitudes/${solicitudId}/articulos/${articuloId}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar artículo' };
    }
  },

  updateBatch: async (articulos) => {
    try {
      const response = await api.put('/solicitudes/articulos/batch', { articulos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar artículos' };
    }
  }
};

// ===== COTIZACIONES SERVICE =====
export const cotizacionesService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/cotizaciones', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener cotizaciones' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/cotizaciones/${id}/detalle`);
      return response.data;
    } catch (errorDetalle) {
      if (errorDetalle.response?.status === 404 || errorDetalle.response?.status === 500) {
        try {
          const response = await api.get(`/cotizaciones/${id}`);
          return response.data;
        } catch (error) {
          throw error.response?.data || { message: 'Error al obtener cotización' };
        }
      }
      throw errorDetalle.response?.data || { message: 'Error al obtener detalle de cotización' };
    }
  },

  getDetalle: async (id) => {
    try {
      const response = await api.get(`/cotizaciones/${id}/detalle`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener detalle de cotización' };
    }
  },

  getConfig: async () => {
    try {
      const response = await api.get('/cotizaciones/config');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener configuración' };
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/cotizaciones/stats/general');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estadísticas' };
    }
  },

  getCalculoAnticipo: async (id) => {
    try {
      const response = await api.get(`/cotizaciones/${id}/calculo-anticipo`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener cálculo de anticipo' };
    }
  },

  crearDesdeSolicitud: async (solicitudId, datos = {}) => {
    try {
      const response = await api.post(`/cotizaciones/desde-solicitud/${solicitudId}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear cotización desde solicitud' };
    }
  },

  crearDesdeSolicitudes: async (solicitudIds, datos = {}) => {
    try {
      const response = await api.post('/cotizaciones/desde-solicitudes', { solicitud_ids: solicitudIds, ...datos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear cotización desde varias solicitudes' };
    }
  },

  combinar: async (cotizacionIds) => {
    try {
      const response = await api.post('/cotizaciones/combinar', { cotizacion_ids: cotizacionIds });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al combinar cotizaciones' };
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/cotizaciones/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar cotización' };
    }
  },

  updateArticulo: async (cotizacionId, articuloId, datos) => {
    try {
      const response = await api.put(`/cotizaciones/${cotizacionId}/articulos/${articuloId}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar artículo' };
    }
  },

  updateArticulosBulk: async (cotizacionId, articulos) => {
    try {
      const response = await api.put(`/cotizaciones/${cotizacionId}/articulos/bulk`, { articulos });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar artículos masivamente' };
    }
  },

  confirmar: async (cotizacionId) => {
    try {
      const response = await api.post(`/cotizaciones/${cotizacionId}/confirmar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al confirmar cotización' };
    }
  },

  rechazar: async (cotizacionId, motivo) => {
    try {
      const response = await api.post(`/cotizaciones/${cotizacionId}/rechazar`, { motivo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al rechazar cotización' };
    }
  },

  actualizarEstado: async (cotizacionId, estado) => {
    try {
      const response = await api.put(`/cotizaciones/${cotizacionId}/estado`, { estado });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar estado' };
    }
  },

  enviarPorEmail: async (cotizacionId, datosEmail) => {
    try {
      const response = await api.post(`/cotizaciones/${cotizacionId}/enviar-email`, datosEmail);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al enviar email' };
    }
  },

  aplicarISLR: async (cotizacionId, datosISLR) => {
    try {
      const response = await api.put(`/cotizaciones/${cotizacionId}/islr`, datosISLR);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al aplicar ISLR' };
    }
  },

  quitarISLR: async (cotizacionId) => {
    try {
      const response = await api.delete(`/cotizaciones/${cotizacionId}/islr`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al quitar ISLR' };
    }
  },

  getPorEmpresa: async () => {
    try {
      const response = await api.get('/cotizaciones/por-empresa');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener cotizaciones por empresa' };
    }
  },

  enviarFacturaPorEmail: async (cotizacionId, datosEmail) => {
    try {
      const response = await api.post(`/cotizaciones/${cotizacionId}/enviar-factura-email`, datosEmail);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al enviar factura por email' };
    }
  },

  pasarAFactura: async (cotizacionId, data = {}) => {
    try {
      const response = await api.post(`/cotizaciones/${cotizacionId}/pasar-a-factura`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al pasar cotización a facturación' };
    }
  },

  actualizarISLRFactura: async (cotizacionId, datosISLR) => {
    try {
      const response = await api.put(`/cotizaciones/${cotizacionId}/islr`, datosISLR);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar ISLR' };
    }
  },

  quitarISLRFactura: async (cotizacionId) => {
    try {
      const response = await api.delete(`/cotizaciones/${cotizacionId}/islr`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al quitar ISLR' };
    }
  }
};

// ===== COMPRAS PENDIENTES SERVICE =====
export const comprasPendientesService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/compras-pendientes', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener compras pendientes' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/compras-pendientes/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener compra pendiente' };
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/compras-pendientes', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear compra pendiente' };
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/compras-pendientes/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar compra pendiente' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/compras-pendientes/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar compra pendiente' };
    }
  },

  batchDesdeCotizacion: async (cotizacionIdOrData, cotizacionNumero, clienteNombre, empresaNombre, articulos) => {
    try {
      let payload;
      if (typeof cotizacionIdOrData === 'object') {
        payload = cotizacionIdOrData;
      } else {
        payload = {
          cotizacion_id: cotizacionIdOrData,
          cotizacion_numero: cotizacionNumero,
          cliente_nombre: clienteNombre,
          empresa_nombre: empresaNombre,
          articulos: articulos.map(art => ({
            articulo_id: art.articulo_id || art.id,
            producto: art.nombre || art.articulo_nombre,
            cantidad: art.cantidad,
            cantidad_faltante: art.cantidad,
            descripcion: art.descripcion || `Sin stock - Vía: ${art.via_compra || 'nacional'}`,
            tipo: art.via_compra || 'nacional',
            link_proveedor: art.link_proveedor || '',
            prioridad: 'alta'
          }))
        };
      }
      const response = await api.post('/compras-pendientes/batch-desde-cotizacion', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear compras pendientes desde cotización' };
    }
  },

  createDesdeCotizacion: async (data) => {
    try {
      const response = await api.post('/compras-pendientes/desde-cotizacion', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear compra pendiente desde cotización' };
    }
  }
};

// ===== FACTURAS SERVICE =====
export const facturasService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/facturas', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener facturas' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/facturas/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener factura' };
    }
  },

  getConfig: async () => {
    try {
      const response = await api.get('/facturas/config');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener configuración de facturas' };
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/facturas', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear factura' };
    }
  },

  createDesdeCotizacion: async (cotizacionId, data) => {
    try {
      const response = await api.post(`/facturas/desde-cotizacion/${cotizacionId}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear factura desde cotización' };
    }
  },

  updateEstado: async (id, estado) => {
    try {
      const response = await api.patch(`/facturas/${id}/estado`, { estado });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar estado de factura' };
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/facturas/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar factura' };
    }
  },

  emitir: async (id, data = {}) => {
    try {
      const response = await api.post(`/facturas/${id}/emitir`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al emitir factura' };
    }
  },

  descargarPdf: async (id) => {
    try {
      const response = await api.get(`/facturas/${id}/pdf`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw new Error('Error al descargar el PDF de la factura');
    }
  }
};

// ===== VALIDACIONES DE PAGO =====
// Mapa { [cotizacion_id|factura_id]: {...} }. El PUT es upsert: solo
// toca las claves que vengan en el body, no borra el resto.
export const validacionesPagoService = {
  getAll: async () => {
    try {
      const response = await api.get('/validaciones-pago');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener validaciones de pago' };
    }
  },

  guardar: async (validaciones) => {
    try {
      const response = await api.put('/validaciones-pago', validaciones);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar la validación de pago' };
    }
  }
};

// ===== FACTURAS DE COMPRA =====
export const facturasCompraService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/facturas-compra', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener facturas de compra' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/facturas-compra/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener factura de compra' };
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/facturas-compra/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener estadísticas de facturas de compra' };
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/facturas-compra', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al registrar factura de compra' };
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/facturas-compra/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar factura de compra' };
    }
  },

  cambiarEstado: async (id, estado) => {
    try {
      const response = await api.patch(`/facturas-compra/${id}/estado`, { estado });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar estado de factura de compra' };
    }
  },

  registrarPago: async (id, datos) => {
    try {
      const response = await api.patch(`/facturas-compra/${id}/pago`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al registrar el pago' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/facturas-compra/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar factura de compra' };
    }
  }
};

// ===== ARCHIVOS ADJUNTOS DE FACTURAS DE COMPRA =====
export const facturasCompraArchivosService = {
  getAll: async (facturaCompraId) => {
    try {
      const response = await api.get(`/facturas-compra-archivos/${facturaCompraId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener archivos adjuntos' };
    }
  },

  subir: async (facturaCompraId, archivo) => {
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const response = await api.post(`/facturas-compra-archivos/${facturaCompraId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al adjuntar el archivo' };
    }
  },

  eliminar: async (id) => {
    try {
      const response = await api.delete(`/facturas-compra-archivos/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar el archivo' };
    }
  }
};

// ===== CENTROS DE OPERACIÓN =====
export const centrosOperacionService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/centros-operacion', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener centros de operación' };
    }
  }
};

// ===== CONFIGURACIÓN GLOBAL =====
export const configService = {
  getTasaBcv: async (fecha) => {
    try {
      const response = await api.get('/config/tasa-bcv', { params: fecha ? { fecha } : {} });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener la tasa BCV' };
    }
  }
};

// ===== CONCILIACION BANCARIA =====
export const conciliacionBancariaService = {
  getCuentas: async (soloActivas = false) => {
    try {
      const response = await api.get('/conciliacion-bancaria/cuentas', { params: soloActivas ? { activo: 'true' } : {} });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener cuentas bancarias' };
    }
  },

  crearCuenta: async (datos) => {
    try {
      const response = await api.post('/conciliacion-bancaria/cuentas', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear cuenta bancaria' };
    }
  },

  actualizarCuenta: async (id, datos) => {
    try {
      const response = await api.put(`/conciliacion-bancaria/cuentas/${id}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar cuenta bancaria' };
    }
  },

  eliminarCuenta: async (id) => {
    try {
      const response = await api.delete(`/conciliacion-bancaria/cuentas/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar cuenta bancaria' };
    }
  },

  getSaldos: async () => {
    try {
      const response = await api.get('/conciliacion-bancaria/cuentas/saldos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener saldos estimados' };
    }
  },

  previsualizarImportacion: async (archivo) => {
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const response = await api.post('/conciliacion-bancaria/movimientos/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al leer el archivo' };
    }
  },

  importarMovimientos: async (archivo, cuentaBancariaId, mapeo) => {
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      formData.append('cuenta_bancaria_id', cuentaBancariaId);
      formData.append('mapeo', JSON.stringify(mapeo));
      const response = await api.post('/conciliacion-bancaria/movimientos/importar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al importar movimientos' };
    }
  },

  getMovimientos: async (params = {}) => {
    try {
      const response = await api.get('/conciliacion-bancaria/movimientos', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener movimientos' };
    }
  },

  getCandidatos: async (movimientoId) => {
    try {
      const response = await api.get(`/conciliacion-bancaria/movimientos/${movimientoId}/candidatos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al buscar candidatos' };
    }
  },

  getDetalle: async (movimientoId) => {
    try {
      const response = await api.get(`/conciliacion-bancaria/movimientos/${movimientoId}/detalle`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener el detalle' };
    }
  },

  conciliar: async (movimientoId, tipo, referenciaId) => {
    try {
      const response = await api.post(`/conciliacion-bancaria/movimientos/${movimientoId}/conciliar`, { tipo, referencia_id: referenciaId });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al conciliar movimiento' };
    }
  },

  desconciliar: async (movimientoId) => {
    try {
      const response = await api.post(`/conciliacion-bancaria/movimientos/${movimientoId}/desconciliar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al desconciliar movimiento' };
    }
  },

  eliminarMovimiento: async (id) => {
    try {
      const response = await api.delete(`/conciliacion-bancaria/movimientos/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar movimiento' };
    }
  },

  conciliarAutomatico: async (cuentaBancariaId) => {
    try {
      const response = await api.post('/conciliacion-bancaria/movimientos/conciliar-automatico', cuentaBancariaId ? { cuenta_bancaria_id: cuentaBancariaId } : {});
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al conciliar automáticamente' };
    }
  },

  generarIngreso: async (movimientoId, overrides = {}) => {
    try {
      const response = await api.post(`/conciliacion-bancaria/movimientos/${movimientoId}/generar-ingreso`, overrides);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al generar el ingreso' };
    }
  }
};

// ===== RETENCIONES =====
export const retencionesService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/retenciones', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener retenciones' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/retenciones/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener retención' };
    }
  },

  crearEmitidaIVA: async (factura_compra_id) => {
    try {
      const response = await api.post('/retenciones/emitida/iva', { factura_compra_id });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear retención de IVA' };
    }
  },

  crearEmitidaISLR: async (factura_compra_id, islr_concepto_id) => {
    try {
      const response = await api.post('/retenciones/emitida/islr', { factura_compra_id, islr_concepto_id });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear retención de ISLR' };
    }
  },

  emitir: async (id) => {
    try {
      const response = await api.patch(`/retenciones/${id}/emitir`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al emitir retención' };
    }
  },

  cambiarEstado: async (id, estado) => {
    try {
      const response = await api.patch(`/retenciones/${id}/estado`, { estado });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar estado de retención' };
    }
  },

  getResumenPendientes: async () => {
    try {
      const response = await api.get('/retenciones/resumen/pendientes');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener el resumen de retenciones' };
    }
  },

  marcarPagada: async (id) => {
    try {
      const response = await api.patch(`/retenciones/${id}/pagada`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al marcar la retención como pagada' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/retenciones/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar retención' };
    }
  }
};

// ===== ISLR SERVICE =====
export const islrConceptosService = {
  getAll: async () => {
    try {
      const response = await api.get('/islr-conceptos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener conceptos ISLR' };
    }
  },

  getRecaudos: async (params = {}) => {
    try {
      const response = await api.get('/recaudos', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener recaudos' };
    }
  }
};

// ===== VEHICULOS SERVICE =====
export const vehiculosService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/vehiculos', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener vehículos' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/vehiculos/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener vehículo' };
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/vehiculos', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear vehículo' };
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/vehiculos/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar vehículo' };
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/vehiculos/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar vehículo' };
    }
  }
};

// ===== REPORTES SERVICE =====
export const reportesService = {
  getRentabilidad: async (params = {}) => {
    try {
      const response = await api.get('/reportes/rentabilidad', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener rentabilidad' };
    }
  },

  getPipeline: async () => {
    try {
      const response = await api.get('/reportes/pipeline');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener pipeline' };
    }
  },

  getRentabilidadVehiculos: async () => {
    try {
      const response = await api.get('/reportes/rentabilidad-vehiculos');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener rentabilidad por vehículo' };
    }
  },

  getLibroVentas: async (params = {}) => {
    try {
      const response = await api.get('/reportes/libro-ventas', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener libro de ventas' };
    }
  },

  getLibroCompras: async (params = {}) => {
    try {
      const response = await api.get('/reportes/libro-compras', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener libro de compras' };
    }
  },

  getPercepcion: async (params = {}) => {
    try {
      const response = await api.get('/reportes/percepcion', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener percepción' };
    }
  },

  getEgresos: async (params = {}) => {
    try {
      const response = await api.get('/reportes/egresos', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener egresos' };
    }
  }
};

// ===== EGRESOS (ledger, creación manual) =====
export const egresosService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/egresos', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener egresos' };
    }
  },

  crear: async (datos) => {
    try {
      const response = await api.post('/egresos', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear el egreso' };
    }
  }
};

// ===== COMPROMISOS DE PAGO (flujo de caja) =====
export const compromisosPagoService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/compromisos-pago', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener compromisos de pago' };
    }
  },

  getResumen: async (dias = 7) => {
    try {
      const response = await api.get('/compromisos-pago/resumen', { params: { dias } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener el resumen' };
    }
  },

  crear: async (datos) => {
    try {
      const response = await api.post('/compromisos-pago', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear el compromiso de pago' };
    }
  },

  marcarPagado: async (id) => {
    try {
      const response = await api.patch(`/compromisos-pago/${id}/pagado`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al marcar como pagado' };
    }
  },

  cancelar: async (id) => {
    try {
      const response = await api.patch(`/compromisos-pago/${id}/cancelar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cancelar' };
    }
  },

  eliminar: async (id) => {
    try {
      const response = await api.delete(`/compromisos-pago/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar' };
    }
  }
};

// ===== PARAMETROS FISCALES SERVICE =====
export const parametrosFiscalesService = {
  getClaves: async () => {
    try {
      const response = await api.get('/parametros-fiscales');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener catálogo de parámetros fiscales' };
    }
  },

  listar: async (clave, params = {}) => {
    try {
      const response = await api.get(`/parametros-fiscales/${clave}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al listar el parámetro fiscal' };
    }
  },

  historial: async (clave, id) => {
    try {
      const response = await api.get(`/parametros-fiscales/${clave}/${id}/historial`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener el historial' };
    }
  },

  nuevaVigencia: async (clave, datos) => {
    try {
      const response = await api.post(`/parametros-fiscales/${clave}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al registrar la nueva vigencia' };
    }
  }
};

// Con responseType blob el cuerpo del error llega como Blob: se lee para no
// perder el mensaje del backend (por ejemplo, el 409 de factura sin adjuntar).
const errorDeBlob = async (error, porDefecto) => {
  try {
    const data = error.response?.data;
    const cuerpo = data instanceof Blob ? JSON.parse(await data.text()) : data;
    if (cuerpo?.message) return new Error(cuerpo.message);
  } catch { /* cuerpo no JSON */ }
  return new Error(porDefecto);
};

// Punto V Manager: el backend del ERP hace de intermediario hacia la API de
// Punto V (tokengaman). El navegador nunca habla con Punto V directo.
export const puntovService = {
  // incluirSinBase: también los clientes cuya base no existe en el servidor de Punto V.
  listarClientes: async ({ incluirSinBase = false } = {}) => {
    try {
      const response = await api.get('/puntov/clientes', { params: incluirSinBase ? { incluir_sin_base: 1 } : {} });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener los clientes de Punto V' };
    }
  },

  suspenderCliente: async (baseDatos, motivo) => {
    try {
      const response = await api.post(`/puntov/clientes/${encodeURIComponent(baseDatos)}/suspender`, { motivo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al suspender el cliente' };
    }
  },

  reactivarCliente: async (baseDatos) => {
    try {
      const response = await api.post(`/puntov/clientes/${encodeURIComponent(baseDatos)}/reactivar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al reactivar el cliente' };
    }
  },

  crearCliente: async (datos) => {
    try {
      const response = await api.post('/puntov/clientes', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear el cliente en Punto V' };
    }
  },

  cargarAlmacenPrueba: async (baseDatos, { conStock = true } = {}) => {
    try {
      const response = await api.post(`/puntov/clientes/${encodeURIComponent(baseDatos)}/almacen-prueba`, { con_stock: conStock });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cargar el almacén de prueba' };
    }
  },

  archivarCliente: async (baseDatos, motivo) => {
    try {
      const response = await api.post(`/puntov/clientes/${encodeURIComponent(baseDatos)}/archivar`, { motivo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al archivar el cliente' };
    }
  },

  restaurarCliente: async (baseDatos) => {
    try {
      const response = await api.delete(`/puntov/clientes/${encodeURIComponent(baseDatos)}/archivar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al restaurar el cliente' };
    }
  },

  listarMaquinas: async (idDuenio) => {
    try {
      const response = await api.get(`/puntov/clientes/${idDuenio}/maquinas`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener las máquinas del cliente' };
    }
  },

  resumenCobros: async () => {
    try {
      const response = await api.get('/puntov/cobros');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar el estado de pagos' };
    }
  },

  listarCotizaciones: async () => {
    try {
      const response = await api.get('/puntov/cotizaciones');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener las cotizaciones de Punto V' };
    }
  },

  crearCotizacionLibre: async (datos) => {
    try {
      const response = await api.post('/puntov/cotizaciones-libres', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear la cotización' };
    }
  },

  listarSolicitudesFactura: async () => {
    try {
      const response = await api.get('/puntov/solicitudes-factura');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener las solicitudes de factura' };
    }
  },

  adjuntarFactura: async (solicitudId, archivo, facturaNumero) => {
    try {
      const datos = new FormData();
      datos.append('archivo', archivo);
      if (facturaNumero) datos.append('factura_numero', facturaNumero);
      const response = await api.post(`/puntov/solicitudes-factura/${solicitudId}/factura`, datos, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al adjuntar la factura' };
    }
  },

  // Devuelve el PDF como Blob: la descarga exige la sesión del ERP.
  descargarFactura: async (solicitudId) => {
    try {
      const response = await api.get(`/puntov/solicitudes-factura/${solicitudId}/factura`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw await errorDeBlob(error, 'No se pudo descargar la factura');
    }
  },

  enviarFactura: async (solicitudId, correo) => {
    try {
      const response = await api.post(`/puntov/solicitudes-factura/${solicitudId}/enviar`, correo ? { correo } : {});
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al enviar la factura' };
    }
  },

  // PDF de la cotización tal como le llega al cliente (Blob; requiere sesión).
  verCotizacionPdf: async (cotizacionId) => {
    try {
      const response = await api.get(`/puntov/cotizaciones/${cotizacionId}/pdf`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw await errorDeBlob(error, 'No se pudo abrir la cotización');
    }
  },

  reenviarMensualidad: async (billingInvoiceId) => {
    try {
      const response = await api.post(`/puntov/mensualidades/${billingInvoiceId}/reenviar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al reenviar la cotización' };
    }
  },

  reenviarCotizacionLibre: async (cotizacionId) => {
    try {
      const response = await api.post(`/puntov/cotizaciones-libres/${cotizacionId}/reenviar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al reenviar la cotización' };
    }
  },

  advertenciaInactivarMaquina: async (empresa, idMaquina) => {
    try {
      const response = await api.get('/puntov/maquinas/advertencia-inactivar', { params: { empresa, id_maquina: idMaquina } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al revisar los pagos de la máquina' };
    }
  },

  cambiarEstadoMaquina: async (empresa, idMaquina, activa) => {
    try {
      const response = await api.put('/puntov/maquinas/estado', { empresa, id_maquina: idMaquina, activa });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar el estado de la máquina' };
    }
  },

  // alertaMin (opcional): minutos sin vender para que una máquina salga en alerta.
  ventas: async (empresa, desde, hasta, { alertaMin } = {}) => {
    try {
      const response = await api.get('/puntov/ventas', { params: { empresa, desde, hasta, ...(alertaMin ? { alerta_min: alertaMin } : {}) } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar las ventas' };
    }
  },

  // datos: { tipo: 'zona_propia'|'cliente'|null, zona, responsable }. tipo null la quita.
  clasificarCliente: async (baseDatos, datos) => {
    try {
      const response = await api.put(`/puntov/clientes/${encodeURIComponent(baseDatos)}/clasificacion`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar la clasificación' };
    }
  },

  resumenDireccion: async (mes,{ alertaMin, refrescar = false } = {}) => {
    try {
      const response = await api.get('/puntov/resumen-direccion', {
        params: { mes, ...(alertaMin ? { alerta_min: alertaMin } : {}), ...(refrescar ? { refrescar: 1 } : {}) }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al armar el resumen para dirección' };
    }
  },

  mensualidad: async (empresa, periodo) => {
    try {
      const response = await api.get('/puntov/mensualidad', { params: { empresa, periodo } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar la mensualidad' };
    }
  },

  // Devuelve el .xlsx como Blob (la descarga exige la sesión del ERP).
  ventasExcel: async (empresa, desde, hasta) => {
    try {
      const response = await api.get('/puntov/ventas/excel', { params: { empresa, desde, hasta }, responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw await errorDeBlob(error, 'No se pudo generar el Excel de ventas');
    }
  },

  cuentasCobro: async () => {
    try {
      const response = await api.get('/puntov/cobro/cuentas');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar la configuración de cobro' };
    }
  },

  guardarCuentaCobro: async (baseDatos, datos) => {
    try {
      const response = await api.put(`/puntov/cobro/cuentas/${encodeURIComponent(baseDatos)}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar la configuración de cobro' };
    }
  },

  verificarCorreoFacturacion: async (baseDatos, correo) => {
    try {
      const response = await api.put(`/puntov/cobro/cuentas/${encodeURIComponent(baseDatos)}/correo`, { correo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al verificar el correo de facturación' };
    }
  },

  habilitarVerificacionCorreo: async (baseDatos, habilitada) => {
    try {
      const response = await api.put(`/puntov/cobro/cuentas/${encodeURIComponent(baseDatos)}/correo/verificacion`, { habilitada });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar la verificación de correo' };
    }
  },

  habilitarCobroTodas: async (empresas) => {
    try {
      const response = await api.post('/puntov/cobro/cuentas/habilitar-todas', { empresas });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al activar el cobro de todas las cuentas' };
    }
  },

  cortesias: async (baseDatos) => {
    try {
      const response = await api.get('/puntov/cortesias', { params: baseDatos ? { empresa: baseDatos } : {} });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar las cortesías' };
    }
  },

  maquinasACobrar: async (baseDatos) => {
    try {
      const response = await api.get(`/puntov/cobro/cuentas/${encodeURIComponent(baseDatos)}/maquinas`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar las máquinas a cobrar' };
    }
  },

  guardarMaquinaACobrar: async (baseDatos, idMaquina, cobrar, motivo) => {
    try {
      const response = await api.put(`/puntov/cobro/cuentas/${encodeURIComponent(baseDatos)}/maquinas/${encodeURIComponent(idMaquina)}`, { cobrar, motivo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al guardar la máquina a cobrar' };
    }
  },

  historialCobros: async (baseDatos) => {
    try {
      const response = await api.get(`/puntov/cobro/cuentas/${encodeURIComponent(baseDatos)}/historial`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar el historial de cobros' };
    }
  },

  otorgarCortesia: async (baseDatos, periodo, motivo) => {
    try {
      const response = await api.post(`/puntov/cortesias/${encodeURIComponent(baseDatos)}`, { periodo, motivo });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al registrar la cortesía' };
    }
  },

  revocarCortesia: async (id) => {
    try {
      const response = await api.delete(`/puntov/cortesias/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al revocar la cortesía' };
    }
  },

  diagnosticoBases: async () => {
    try {
      const response = await api.get('/puntov/bases/diagnostico');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al diagnosticar las bases' };
    }
  },

  usuariosCliente: async (baseDatos) => {
    try {
      const response = await api.get(`/puntov/clientes/${encodeURIComponent(baseDatos)}/usuarios`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cargar los usuarios del cliente' };
    }
  },

  crearUsuarioCliente: async (baseDatos, datos) => {
    try {
      const response = await api.post(`/puntov/clientes/${encodeURIComponent(baseDatos)}/usuarios`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear el usuario' };
    }
  },

  actualizarUsuarioCliente: async (baseDatos, id, datos) => {
    try {
      const response = await api.put(`/puntov/clientes/${encodeURIComponent(baseDatos)}/usuarios/${id}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar el usuario' };
    }
  },

  rolesCliente: async (baseDatos) => {
    try {
      const response = await api.get(`/puntov/clientes/${encodeURIComponent(baseDatos)}/roles`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cargar los roles del cliente' };
    }
  },

  crearRolCliente: async (baseDatos, datos) => {
    try {
      const response = await api.post(`/puntov/clientes/${encodeURIComponent(baseDatos)}/roles`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear el rol' };
    }
  },

  actualizarRolCliente: async (baseDatos, id, datos) => {
    try {
      const response = await api.put(`/puntov/clientes/${encodeURIComponent(baseDatos)}/roles/${id}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar el rol' };
    }
  },

  eliminarRolCliente: async (baseDatos, id) => {
    try {
      const response = await api.delete(`/puntov/clientes/${encodeURIComponent(baseDatos)}/roles/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar el rol' };
    }
  },

  permisosCliente: async (baseDatos) => {
    try {
      const response = await api.get(`/puntov/clientes/${encodeURIComponent(baseDatos)}/permisos`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cargar los permisos de Punto V' };
    }
  },

  repararBase: async (baseDatos) => {
    try {
      const response = await api.post(`/puntov/bases/${encodeURIComponent(baseDatos)}/reparar`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al reparar la base' };
    }
  },

  problemasMaquinas: async (baseDatos) => {
    try {
      const response = await api.get(`/puntov/bases/${encodeURIComponent(baseDatos)}/maquinas/problemas`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al revisar las máquinas' };
    }
  },

  // maquinas: [{ id_maquina, acciones: ['renombrar' | 'crear' | 'quitar_duplicados'] }]
  repararMaquinas: async (baseDatos, maquinas) => {
    try {
      const response = await api.post(`/puntov/bases/${encodeURIComponent(baseDatos)}/maquinas/reparar`, { maquinas });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al reparar las máquinas' };
    }
  },

  tiposMaquina: async () => {
    try {
      const response = await api.get('/puntov/tipos-maquina');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener los tipos de máquina' };
    }
  },

  crearMaquina: async (datos) => {
    try {
      const response = await api.post('/puntov/maquinas', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al registrar la máquina' };
    }
  }
};

// ===== ÓRDENES DE ENTREGA (A6) =====
export const chatsService = {
  listar: async () => {
    try {
      const response = await api.get('/chats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener los chats' };
    }
  },

  pendientes: async () => {
    try {
      const response = await api.get('/chats/pendientes');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al contar los chats pendientes' };
    }
  },

  mensajes: async (id) => {
    try {
      const response = await api.get(`/chats/${encodeURIComponent(id)}/mensajes`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener los mensajes' };
    }
  },

  enviar: async (id, mensaje) => {
    try {
      const response = await api.post(`/chats/${encodeURIComponent(id)}/enviar`, { mensaje });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al enviar el mensaje' };
    }
  },

  pausarBot: async (id, pausado, opciones = {}) => {
    try {
      const response = await api.put(`/chats/${encodeURIComponent(id)}/bot`, { pausado, ...opciones });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar el estado del bot' };
    }
  },

  habilitarBot: async (id, habilitado) => {
    try {
      const response = await api.put(`/chats/${encodeURIComponent(id)}/habilitado`, { habilitado });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar la habilitación del bot' };
    }
  },

  // Blob con el archivo del mensaje: se pide por axios (con la cookie de sesion) y no por <img src>.
  media: async (id, mensajeId) => {
    const response = await api.get(`/chats/${encodeURIComponent(id)}/mensajes/${encodeURIComponent(mensajeId)}/media`, { responseType: 'blob' });
    return response.data;
  }
};

export const ordenesEntregaService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/ordenes-entrega', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener las órdenes de entrega' };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/ordenes-entrega/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener la orden de entrega' };
    }
  },

  confirmarEntrega: async (id, datos) => {
    try {
      const response = await api.post(`/ordenes-entrega/${id}/confirmar-entrega`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al confirmar la entrega' };
    }
  },

  enDespacho: async (id) => {
    try {
      const response = await api.post(`/ordenes-entrega/${id}/en-despacho`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar el estado' };
    }
  },

  anular: async (id, datos) => {
    try {
      const response = await api.post(`/ordenes-entrega/${id}/anular`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al anular la orden de entrega' };
    }
  }
};

// ===== INTEGRACIÓN CON SIMCUV =====
// El ERP consulta el estado de la conexión servidor a servidor. Devuelve una
// categoría (`error`) en vez de lanzar por cada fallo del otro lado.
export const simcuvService = {
  estado: async () => {
    try {
      const response = await api.get('/integraciones/simcuv/estado');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al consultar la integración con SIMCUV' };
    }
  }
};


export const documentosVentaService = {
  listar: async ({ maquinaId = null } = {}) => {
    try {
      const params = maquinaId ? { maquina_id: maquinaId } : { generales: true };
      const response = await api.get('/documentos-venta', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener los documentos' };
    }
  },

  subir: async ({ archivo, titulo, tipo, descripcion, maquinaId = null }) => {
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      formData.append('titulo', titulo);
      formData.append('tipo', tipo);
      if (descripcion) formData.append('descripcion', descripcion);
      if (maquinaId) formData.append('maquina_id', maquinaId);
      const response = await api.post('/documentos-venta', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al subir el documento' };
    }
  },

  desactivar: async (id) => {
    try {
      const response = await api.delete(`/documentos-venta/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al desactivar el documento' };
    }
  },

  descargar: async (id) => {
    try {
      const response = await api.get(`/documentos-venta/${id}/archivo`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw new Error('No se pudo descargar el documento');
    }
  }
};

export const maquinasService = {
  listar: async ({ incluirInactivas = false } = {}) => {
    try {
      const response = await api.get('/maquinas', { params: incluirInactivas ? {} : { activo: true } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener las máquinas' };
    }
  },

  obtener: async (id) => {
    try {
      const response = await api.get(`/maquinas/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al obtener la máquina' };
    }
  },

  crear: async (datos) => {
    try {
      const response = await api.post('/maquinas', datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al crear la máquina' };
    }
  },

  actualizar: async (id, datos) => {
    try {
      const response = await api.put(`/maquinas/${id}`, datos);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al actualizar la máquina' };
    }
  },

  desactivar: async (id) => {
    try {
      const response = await api.delete(`/maquinas/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al desactivar la máquina' };
    }
  },

  subirFotos: async (id, archivos) => {
    try {
      const formData = new FormData();
      archivos.forEach((archivo) => formData.append('fotos', archivo));
      const response = await api.post(`/maquinas/${id}/fotos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al subir las fotos' };
    }
  },

  fotoPrincipal: async (id, fotoId) => {
    try {
      const response = await api.put(`/maquinas/${id}/fotos/${fotoId}/principal`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al cambiar la foto principal' };
    }
  },

  eliminarFoto: async (id, fotoId) => {
    try {
      const response = await api.delete(`/maquinas/${id}/fotos/${fotoId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error al eliminar la foto' };
    }
  }
};

export default api;
